import { useState, useCallback, useEffect, useMemo } from "react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { APIError, useAxios } from "../core/_axios";
import { Service } from "../core/_models";
import { getGraphOfServicesOverTime } from "../core/_requests_v2";
import LoadingSpinner from "./LoadingSpinner";
import dayjs from "dayjs";
import { calculateTrendLine, generateTrendLineData } from "../utils/trendLineCalculator";
import { Download } from "lucide-react";

import weekOfYear from "dayjs/plugin/weekOfYear";   // adds .week()/.weekOfYear() + "WW" token
import isoWeek from "dayjs/plugin/isoWeek";      // makes startOf("week") Monday-based (ISO-8601)
import weekday from "dayjs/plugin/weekday";      // lets you tweak weekday(0) etc. if you need it
import dayOfYear from "dayjs/plugin/dayOfYear";  // adds .dayOfYear() method

dayjs.extend(weekOfYear);
dayjs.extend(isoWeek);      // optional but handy for Monday-start calendars
dayjs.extend(weekday);      // optional
dayjs.extend(dayOfYear);    // for year projections

type GroupBy = "" | "week" | "month" | "year";

function groupData(
    data: { date: string; count: number }[],
    groupBy: GroupBy
): { date: string; count: number }[] {
    if (!groupBy) return data;

    const grouped: Record<string, number> = {};

    data.forEach(({ date, count }) => {
        let key = date;
        if (groupBy === "week") {
            key = dayjs(date).startOf("week").format("YYYY-MM-DD");   // e.g. "2025-05-19"
        } else if (groupBy === "month") {
            key = dayjs(date).format("YYYY-MM");
        } else if (groupBy === "year") {
            key = dayjs(date).format("YYYY");
        }
        grouped[key] = (grouped[key] || 0) + count;
    });

    return Object.entries(grouped)
        .map(([date, count]) => ({ date, count }))
        .sort((a, b) => a.date.localeCompare(b.date));
}

export function ServicesGraph({ service }: { service?: Service }) {
    const axios = useAxios();

    const [loading, setLoading] = useState(true);
    const [loadingError, setLoadingError] = useState<string | null>(null);
    const [data, setData] = useState<{ date: string, count: number }[]>([]);
    const [groupBy, setGroupBy] = useState<GroupBy>("month");
    const [showAdvanced, setShowAdvanced] = useState(false);

    const loadServices = useCallback(async () => {
        setLoadingError(null);
        setLoading(true);
        try {
            const servicesOverTime = await getGraphOfServicesOverTime(axios);
            const formatted = servicesOverTime.map((item: any) => ({
                date: item.date,
                count: item.count
            }));
            setData(formatted);
        } catch (exception) {
            console.error(exception);
            const errorMessage =
                exception instanceof APIError
                    ? exception.message
                    : "An unknown error has occurred. Please try again.";
            setLoadingError(errorMessage);
        } finally {
            setLoading(false);
        }
    }, [axios]);

    useEffect(() => {
        loadServices();
    }, [loadServices]);

    // Fill in missing periods with zeros and add projections
    const fillMissingPeriods = useCallback((data: { date: string; count: number }[]): { date: string; count: number; actualCount?: number; isProjected?: boolean; hideFromGraph?: boolean }[] => {
        if (data.length === 0) return data;

        const result: { date: string; count: number; actualCount?: number; isProjected?: boolean; hideFromGraph?: boolean }[] = [];

        if (groupBy === "month") {
            const monthMap = new Map(data.map(d => [d.date, d.count]));

            // Find the earliest and latest dates
            const dates = data.map(d => d.date).sort();
            const startDate = dayjs(dates[0]);
            const currentMonth = dayjs().format('YYYY-MM');
            const endDate = dayjs(currentMonth);

            // Generate all months from start to current
            let current = startDate;
            while (current.isBefore(endDate) || current.format('YYYY-MM') === currentMonth) {
                const monthKey = current.format('YYYY-MM');
                const count = monthMap.get(monthKey) || 0;

                if (monthKey === currentMonth && count > 0) {
                    const dayOfMonth = dayjs().date();

                    // Hide current month from graph if less than 7 days into the month
                    if (dayOfMonth < 7) {
                        result.push({
                            date: monthKey,
                            count: count,
                            hideFromGraph: true
                        });
                    } else {
                        // For current month, show projected value with actual as metadata
                        const daysInMonth = dayjs().daysInMonth();
                        const projectedCount = Math.round((count / dayOfMonth) * daysInMonth);

                        if (projectedCount > count) {
                            result.push({
                                date: monthKey,
                                count: projectedCount,
                                actualCount: count,
                                isProjected: true
                            });
                        } else {
                            result.push({ date: monthKey, count });
                        }
                    }
                } else {
                    result.push({ date: monthKey, count });
                }

                current = current.add(1, 'month');
            }
        } else if (groupBy === "year") {
            const yearMap = new Map(data.map(d => [d.date, d.count]));
            
            // Find the earliest and latest years
            const years = data.map(d => d.date).sort();
            const startYear = years[0];
            const currentYear = dayjs().format('YYYY');
            
            // Generate all years from start to current
            for (let year = parseInt(startYear); year <= parseInt(currentYear); year++) {
                const yearKey = year.toString();
                const count = yearMap.get(yearKey) || 0;
                
                if (yearKey === currentYear && count > 0) {
                    // For current year, show projected value with actual as metadata
                    const dayOfYear = dayjs().dayOfYear();
                    const daysInYear = dayjs().endOf('year').dayOfYear();
                    const projectedCount = Math.round((count / dayOfYear) * daysInYear);
                    
                    if (projectedCount > count) {
                        result.push({ 
                            date: yearKey, 
                            count: projectedCount, 
                            actualCount: count, 
                            isProjected: true 
                        });
                    } else {
                        result.push({ date: yearKey, count });
                    }
                } else {
                    result.push({ date: yearKey, count });
                }
            }
        } else {
            return data;
        }
        
        return result;
    }, [groupBy]);
    
    const groupedData = useMemo(() => {
        if (!data || data.length === 0) return [];
        return fillMissingPeriods(groupData(data, groupBy));
    }, [data, groupBy, fillMissingPeriods]);
    
    // Calculate trend line data (only for month view)
    const { trendLineData, trendInfo } = useMemo(() => {
        if (groupBy !== 'month') return { trendLineData: null, trendInfo: null };
        const trend = calculateTrendLine(groupedData as any);
        if (trend) {
            // Only generate trend line data if the fit is good enough
            const hasMeaningfulTrend = (trend.exponentialR2 && trend.exponentialR2 > 0.7) ||
                                       (trend.linearR2 && trend.linearR2 > 0.5);

            return {
                trendLineData: hasMeaningfulTrend ? generateTrendLineData(groupedData, trend) : null,
                trendInfo: {
                    type: trend.type,
                    exponentialR2: trend.exponentialR2,
                    linearR2: trend.linearR2,
                    cmgr: trend.cmgr
                }
            };
        }
        return { trendLineData: null, trendInfo: null };
    }, [groupedData, groupBy]);
    
    if (loading) {
        return <LoadingSpinner />;
    }

    if (loadingError) {
        return (
            <div>
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
                    <span className="block sm:inline">{loadingError}</span>
                </div>
                <div className="mt-5">
                    <button className="btn btn-lg btn-light" onClick={() => {
                        setLoadingError(null);
                        loadServices();
                    }}>
                        Retry
                    </button>
                </div>
            </div>
        );
    }
    
    // Get current period data for display
    const currentPeriodData = groupedData[groupedData.length - 1];
    
    // Export data as CSV
    const exportData = () => {
        if (!groupedData || groupedData.length === 0) return;
        
        // Prepare CSV content
        const csvContent = [
            'Date,Count', // Header
            ...groupedData.map(item => {
                // Use projected count for last item if it exists, otherwise use regular count
                const count = item.count;
                return `${item.date},${count}`;
            })
        ].join('\n');
        
        // Create blob and download
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        
        // Generate filename with current date and view type
        const timestamp = dayjs().format('YYYY-MM-DD');
        const filename = `services-${groupBy || 'daily'}-${timestamp}.csv`;
        
        link.setAttribute('href', url);
        link.setAttribute('download', filename);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };
    
    // Get current period label and count for subtitle
    const getCurrentPeriodLabel = () => {
        const current = currentPeriodData;
        if (!current) return null;
        
        let label = "";
        let value = current.isProjected ? current.actualCount : current.count;
        let projected = current.isProjected ? current.count : null;
        
        if (groupBy === "") {
            label = "Today";
        } else if (groupBy === "week") {
            label = "This week";
        } else if (groupBy === "month") {
            label = "This month";
        } else if (groupBy === "year") {
            label = "This year";
        }
        
        if (projected) {
            return `${value} ${label.toLowerCase()} • Projected: ${projected}`;
        }
        return `${value} ${label.toLowerCase()}`;
    };

    return (
        <div className="grid gap-5 lg:gap-7.5">
            <div className="card">
                <div className="card-header">
                    <div>
                        <h3 className="card-title">Services Over Time</h3>
                        {currentPeriodData && (
                            <p className="text-sm text-gray-500 mt-1">{getCurrentPeriodLabel()}</p>
                        )}
                    </div>
                    <div className="flex gap-2">
                        <button
                            onClick={() => setGroupBy("")}
                            className={`btn btn-sm ${groupBy === "" ? "btn-primary" : "btn-light"}`}
                        >
                            Day
                        </button>
                        <button
                            onClick={() => setGroupBy("week")}
                            className={`btn btn-sm ${groupBy === "week" ? "btn-primary" : "btn-light"}`}
                        >
                            Week
                        </button>
                        <button
                            onClick={() => setGroupBy("month")}
                            className={`btn btn-sm ${groupBy === "month" ? "btn-primary" : "btn-light"}`}
                        >
                            Month
                        </button>
                        <button
                            onClick={() => setGroupBy("year")}
                            className={`btn btn-sm ${groupBy === "year" ? "btn-primary" : "btn-light"}`}
                        >
                            Year
                        </button>
                        {showAdvanced && (
                            <button
                                onClick={exportData}
                                className="btn btn-sm btn-light ml-3"
                                title="Export data as CSV"
                            >
                                <Download className="w-4 h-4" />
                            </button>
                        )}
                        <button
                            onClick={() => setShowAdvanced(!showAdvanced)}
                            className={`btn btn-sm ${showAdvanced ? "btn-warning" : "btn-light"}`}
                            title="Toggle advanced options"
                        >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            </svg>
                        </button>
                    </div>
                </div>
                <div className="card-body">
            <ResponsiveContainer width="100%" height={500}>
                <LineChart data={(trendLineData || groupedData).filter(d => !d.hideFromGraph)}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                        dataKey="date"
                        interval={groupBy === "month" ? 0 : "preserveStartEnd"}
                        angle={groupBy === "month" && groupedData.length > 12 ? -45 : 0}
                        textAnchor={groupBy === "month" && groupedData.length > 12 ? "end" : "middle"}
                        height={groupBy === "month" && groupedData.length > 12 ? 60 : undefined}
                    />
                    <YAxis allowDecimals={false} />
                    <Tooltip
                        content={({ active, payload }) => {
                            if (active && payload && payload.length > 0) {
                                const data = payload[0].payload;
                                const trendValue = payload.find((p: any) => p.dataKey === 'trend');
                                return (
                                    <div className="bg-white p-2 border border-gray-300 rounded shadow">
                                        <p className="font-semibold">{data.date}</p>
                                        {data.isProjected ? (
                                            <>
                                                <p className="text-amber-600">Current: {data.actualCount}</p>
                                                <p className="text-gray-500">Projected: {data.count}</p>
                                            </>
                                        ) : (
                                            <p>Count: {data.count}</p>
                                        )}
                                        {trendValue && trendValue.value !== undefined && (
                                            <p className="text-gray-400">Trend: {Math.round(Number(trendValue.value))}</p>
                                        )}
                                    </div>
                                );
                            }
                            return null;
                        }}
                    />
                    {/* Trend line - render first so it appears behind the main line */}
                    {trendLineData && (
                        <Line
                            type="natural"
                            dataKey="trend"
                            stroke="#9ca3af"
                            strokeWidth={1.5}
                            strokeDasharray="5 5"
                            dot={false}
                            activeDot={false}
                            isAnimationActive={false}
                        />
                    )}
                    <Line
                        type="monotone"
                        dataKey="count"
                        stroke="#3b82f6"
                        strokeWidth={2}
                        dot={(props: any) => {
                            const { cx, cy, payload, index } = props;
                            if (payload.isProjected) {
                                return <circle key={`dot-projected-${index}`} cx={cx} cy={cy} r={4} fill="#9ca3af" />;
                            }
                            return <circle key={`dot-${index}`} cx={cx} cy={cy} r={3} fill="#3b82f6" />;
                        }}
                        connectNulls={false}
                    />
                    {/* Add actual value dots for current month */}
                    <Line
                        type="monotone"
                        dataKey="actualCount"
                        stroke="transparent"
                        strokeWidth={0}
                        dot={(props: any) => {
                            const { cx, cy, payload, index } = props;
                            if (payload.actualCount) {
                                return <circle key={`dot-actual-${index}`} cx={cx} cy={cy} r={5} fill="#fbbf24" stroke="#f59e0b" strokeWidth={1} />;
                            }
                            return null;
                        }}
                        connectNulls={false}
                        isAnimationActive={false}
                    />
                </LineChart>
            </ResponsiveContainer>
                    {groupBy === 'month' && (groupedData.some(d => d.hideFromGraph) || (trendInfo && trendInfo.cmgr !== undefined)) && (
                        <div className="mt-3">
                            <div className="text-sm">
                                {groupedData.some(d => d.hideFromGraph) && (
                                    <span className="text-gray-500 italic">
                                        Current month will appear on graph after {7 - dayjs().date()} more day{7 - dayjs().date() !== 1 ? 's' : ''}
                                    </span>
                                )}
                                {trendInfo && trendInfo.cmgr !== undefined && (
                                    <>
                                        {groupedData.some(d => d.hideFromGraph) && <span className="text-gray-400 mx-2">•</span>}
                                        <span className="font-semibold text-green-700">
                                            CMGR: {(trendInfo.cmgr * 100).toFixed(1)}%
                                        </span>
                                    </>
                                )}
                            </div>
                            {trendInfo && trendInfo.cmgr !== undefined && (() => {
                                const currentValue = groupedData.filter(d => !d.hideFromGraph && !d.isProjected)[groupedData.filter(d => !d.hideFromGraph && !d.isProjected).length - 1]?.count || 0;
                                const cmgrPercent = trendInfo.cmgr * 100;
                                const projectedIn12Months = Math.round(currentValue * Math.pow(1 + trendInfo.cmgr, 12));

                                const bands = [
                                    { min: -Infinity, max: 0, label: "Declining", color: "text-red-700 bg-red-50" },
                                    { min: 0, max: 5, label: "Steady (0-5%)", color: "text-yellow-700 bg-yellow-50" },
                                    { min: 5, max: 15, label: "Strong (5-15%)", color: "text-green-600 bg-green-50" },
                                    { min: 15, max: 30, label: "Exceptional (15-30%)", color: "text-green-700 bg-green-100" },
                                    { min: 30, max: Infinity, label: "Outstanding (30%+)", color: "text-green-800 bg-green-200" }
                                ];

                                const currentBand = bands.find(b => cmgrPercent >= b.min && cmgrPercent < b.max);

                                return (
                                    <div className="mt-2 p-3 bg-blue-50 rounded-lg text-sm">
                                        <div className="font-semibold text-gray-700 mb-2">Growth Performance</div>
                                        <div className="text-gray-600 space-y-2">
                                            <div className="flex flex-wrap gap-2">
                                                {bands.map((band, idx) => {
                                                    const isActive = band === currentBand;
                                                    return (
                                                        <span
                                                            key={idx}
                                                            className={`px-2 py-1 rounded text-xs ${isActive ? `${band.color} font-semibold ring-2 ring-offset-1 ${band.color.includes('red') ? 'ring-red-400' : band.color.includes('yellow') ? 'ring-yellow-400' : 'ring-green-400'}` : 'bg-gray-100 text-gray-500'}`}
                                                        >
                                                            {band.label}
                                                        </span>
                                                    );
                                                })}
                                            </div>
                                            <div className="pt-2 border-t border-blue-200">
                                                <span className="font-medium">12-Month Projection:</span> If we maintain this {cmgrPercent.toFixed(1)}% monthly growth rate, we'll serve approximately <span className="font-semibold text-green-700">{projectedIn12Months.toLocaleString()}</span> services per month in one year (up from {currentValue.toLocaleString()} today).
                                            </div>
                                            <div className="text-xs text-gray-500 mt-2">
                                                <a
                                                    href="https://www.wallstreetprep.com/knowledge/compound-monthly-growth-rate-cmgr/"
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="text-blue-600 underline hover:text-blue-800"
                                                >
                                                    Learn more about CMGR
                                                </a>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })()}
                        </div>
                    )}
                    {showAdvanced && trendInfo && groupBy === 'month' && (
                        <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                            <div className="text-sm text-gray-600">
                                <span className="font-semibold">Trend Analysis:</span>
                                <span className="ml-3">
                                    Model: {trendInfo.type === 'exponential' ? 'Exponential' : 'Linear'}
                                </span>
                                <span className="ml-4">
                                    Linear R²: {(trendInfo.linearR2 || 0).toFixed(3)}
                                </span>
                                <span className="ml-4">
                                    Exponential R²: {(trendInfo.exponentialR2 || 0).toFixed(3)}
                                </span>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
